import {
    Button,
    Center,
    ListItem,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    OrderedList,
    Spinner,
    Text,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import Keycloak from 'keycloak-js';
import { useEffect, useState } from 'react';
import { FiShare } from 'react-icons/fi';
import { redirect } from 'react-router';
import {
    createBrowserRouter,
    createRoutesFromElements,
    Navigate,
    Outlet,
    Route,
    RouterProvider,
    Routes,
    ScrollRestoration,
} from 'react-router-dom';
import { AuthenticatedComponent, isAuthenticated } from './auth/keycloak';
import { FeatureFlagContextProvider } from './context/FeatureFlagContext';
import { NotificationContextProvider } from './context/NotificationContext';
import { PageDataContextProvider } from './context/PageDataContext';
import './firebase/firebase';
import { ConductCode } from './pages/ConductCode';
import { DashboardNewsPage } from './pages/DashboardNewsPage';
import { DashboardPage } from './pages/DashboardPage';
import { DashboardSessionsPage } from './pages/DashboardSessionsPage';
import { DashboardTransactionsPage } from './pages/DashboardTransactionsPage';
import { DashboardWalletPage } from './pages/DashboardWalletPage';
import { DiscoverPage } from './pages/DiscoverPage';
import { EditProfile } from './pages/EditProfile';
import { FollowingPage } from './pages/FollowingPage';
import { GamePage } from './pages/GamePage';
import { GamesListPage } from './pages/GamesListPage';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { LoginRedirectPage } from './pages/LoginRedirectPage';
import { PrivacyAppendix } from './pages/PrivacyAppendix';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { ProfileFollowers } from './pages/ProfileFollowers';
import { ProfilePage } from './pages/ProfilePage';
import { ProfileSubscriptions } from './pages/ProfileSubscriptions';
import { ProfileVault } from './pages/ProfileVault';
import { PublicProfilePage } from './pages/PublicProfilePage';
import { RedirectPage } from './pages/RedirectPage';
import { Settings } from './pages/Settings';
import { SetupMembership } from './pages/SetupMembership';
import { SetupProfilePage } from './pages/SetupProfilePage';
import { StorePage } from './pages/StorePage';
import { TermsConditions } from './pages/Terms';
import './styles/style.scss';
import './styles/updated-style.scss';
import { isInStandaloneMode, isIos } from './util/platform';

export const getBaseUrl = (): string => {
    const host = window?.location?.hostname;
    const port = window?.location?.port;
    const protocol = window?.location.protocol;

    if (!!host && !!protocol) {
        if (host === 'localhost') {
            if (!!port) {
                return `${protocol}//${host}:${port}/`;
            }
        } else {
            return `${protocol}//${host}/`;
        }
    }
    return '';
};

const handleLoginRedirect = (source: string, keycloak: Keycloak) => {
    const sourcePathStorageKey = 'streamsix_keycloak_redir_source';
    const redirectUrl = `${getBaseUrl()}login-redirect`;
    if (redirectUrl === '') {
        keycloak?.login();
    } else {
        localStorage.setItem(sourcePathStorageKey, source);
        keycloak?.login({ redirectUri: redirectUrl });
    }
};

const SpinnerLayout = () => {
    return (
        <Center width="100%" height="100%">
            <Spinner data-cy="loading" />
        </Center>
    );
};

/**
 * This handles the pages that are for authenticated users only
 */
const AuthenticatedPagesHandler: AuthenticatedComponent = ({ keycloak }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const authorizedRoot = () => {
        return (
            <>
                <Modal isOpen={isModalOpen} isCentered onClose={() => setIsModalOpen(false)}>
                    <ModalOverlay />
                    <ModalContent className={'installation-modal-content'} maxWidth={'300px'}>
                        <ModalHeader className={'installation-modal-header'}>
                            Add To Home Screen
                        </ModalHeader>
                        <ModalBody className={'installation-modal-body'}>
                            <Text mb={'1em'}>
                                To add this app to your homescreen, do the following:
                            </Text>
                            <OrderedList>
                                <ListItem className={'ios-svg-li'}>
                                    <Text display={'flex'}>Tap the 'Share' icon {<FiShare />}</Text>
                                </ListItem>
                                <ListItem>Scroll down and tap 'Add To Home Screen'</ListItem>
                                <ListItem>
                                    In the next screen, give the app a nickname and then hit 'Add'
                                    in the upper right corner
                                </ListItem>
                            </OrderedList>
                        </ModalBody>
                        <ModalFooter className={'installation-modal-footer'}>
                            <Button onClick={() => setIsModalOpen(false)}>Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
                <Outlet />
            </>
        );
    };

    useEffect(() => {
        if (isIos() && !isInStandaloneMode()) {
            setIsModalOpen(true);
        }
    }, []);

    return (
        <Routes>
            <Route element={authorizedRoot()}>
                <Route path="/following" element={<FollowingPage keycloak={keycloak} />} />
                <Route path="/store" element={<StorePage keycloak={keycloak} />} />
                <Route path="/profile/*" element={<ProfilePage keycloak={keycloak} />} />
                <Route path="/dashboard" element={<DashboardPage keycloak={keycloak} />} />
                <Route
                    path="/dashboard/sessions"
                    element={<DashboardSessionsPage keycloak={keycloak} />}
                />
                <Route
                    path="/dashboard/transactions"
                    element={<DashboardTransactionsPage keycloak={keycloak} />}
                />
                <Route
                    path="/dashboard/wallet"
                    element={<DashboardWalletPage keycloak={keycloak} />}
                />
                <Route path="/dashboard/news" element={<DashboardNewsPage />} />
                <Route path="/follow-stats" element={<ProfileFollowers keycloak={keycloak} />} />
                <Route
                    path="/creator-subscriptions"
                    element={<ProfileSubscriptions keycloak={keycloak} />}
                />
                <Route path="/profile/edit" element={<EditProfile keycloak={keycloak} />} />
                <Route path="/vault" element={<ProfileVault keycloak={keycloak} />} />
                <Route path="/setup/profile" element={<SetupProfilePage keycloak={keycloak} />} />
                <Route path="/setup/membership" element={<SetupMembership keycloak={keycloak} />} />
                <Route path="/login-redirect" element={<LoginRedirectPage keycloak={keycloak} />} />

                <Route path="/" element={<RedirectPage />} />
                <Route path="*" element={<Navigate to="/" />} />
            </Route>
        </Routes>
    );
};

function App() {
    const { keycloak, initialized } = useKeycloak();

    const root = () => {
        return (
            <PageDataContextProvider>
                <NotificationContextProvider>
                    <FeatureFlagContextProvider>
                        <ScrollRestoration />
                        <Outlet />
                    </FeatureFlagContextProvider>
                </NotificationContextProvider>
            </PageDataContextProvider>
        );
    };

    const UnauthenticatedPagesHandler = (props: { source: string; keycloak: Keycloak }) => {
        const unauthorizedRoot = () => {
            return (
                <>
                    <Outlet />
                </>
            );
        };

        return (
            <Routes>
                <Route element={unauthorizedRoot()}>
                    <Route
                        path="*"
                        handle={handleLoginRedirect(props.source, props.keycloak)}
                        element={<></>}
                    />
                </Route>
            </Routes>
        );
    };

    const router = createBrowserRouter([
        ...createRoutesFromElements(
            initialized ? (
                <Route element={root()}>
                    <Route path="/games" element={<GamesListPage />} />
                    <Route path="/hosts" element={<DiscoverPage />} />
                    <Route path="/leaderboards" element={<LeaderboardPage />} />
                    <Route path="/user/:userID" element={<PublicProfilePage />} />
                    <Route path="/game/:gameID" element={<GamePage />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/privacy-appendix" element={<PrivacyAppendix />} />
                    <Route path="/terms-and-conditions" element={<TermsConditions />} />
                    <Route path="/code-of-conduct" element={<ConductCode />} />

                    {isAuthenticated(keycloak) ? (
                        <>
                            <Route path="/" element={<RedirectPage />} />
                            <Route
                                path="*"
                                element={<AuthenticatedPagesHandler keycloak={keycloak} />}
                            />
                        </>
                    ) : (
                        <>
                            <Route path="/" loader={() => redirect('/hosts')} />
                            <Route
                                path="*"
                                element={
                                    <UnauthenticatedPagesHandler
                                        keycloak={keycloak}
                                        source={window.location.pathname}
                                    />
                                }
                            />
                        </>
                    )}
                </Route>
            ) : (
                <Route path="*" element={<SpinnerLayout />} />
            ),
        ),
    ]);

    return <RouterProvider router={router} />;
}

export default App;
